import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Activity,
  Calendar,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Receipt,
  Trophy,
  Users,
  Zap,
} from "lucide-react";
import { FaChevronLeft, FaChevronRight, FaInfo, FaTimes } from "react-icons/fa";
import { TournamentHeader } from "./GameSessionHeader";
import { TournamentActivityList } from "./TournamentActivityList";
import "./Style.css";

const money = (value) => `$${Math.round(Number(value) || 0).toLocaleString()}`;

const parseCustomAllocations = (value) => {
  if (!value) return [];
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch (err) {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((allocation) => ({
      ...allocation,
      total: Number(allocation?.total) || 0,
    }))
    .filter((allocation) => allocation.name);
};

function InitialLoadingScreen() {
  return (
    <div
      className="loading-container"
      style={{ padding: "4rem", textAlign: "center", color: "white" }}
    >
      <h2 style={{ marginBottom: "1.5rem", fontWeight: "700" }}>
        Gathering Data...
      </h2>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div
          className="spinner"
          style={{
            border: "4px solid rgba(255,255,255,0.1)",
            borderTop: "4px solid #6366f1",
            borderRadius: "50%",
            width: "50px",
            height: "50px",
            animation: "spin 1s linear infinite",
          }}
        ></div>
      </div>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const compactMoney = (value) => {
  const num = Number(value) || 0;
  const abs = Math.abs(num);
  if (abs >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return money(num);
};

const localDateKey = (dateValue) => {
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDate = (dateValue) => {
  if (!dateValue) return "";
  return new Date(dateValue).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const chartLabel = (dateValue) => {
  const date = new Date(dateValue);
  return `${date.toLocaleDateString(undefined, { weekday: "short" })}|${date.toLocaleDateString(undefined, { month: "numeric", day: "numeric" })}`;
};

const GRAPH_SERIES = [
  { key: "profit", label: "Profit", color: "#10b981" },
  { key: "total_rake", label: "Rake", color: "#8b5cf6" },
  { key: "costs", label: "Costs", color: "#f43f5e" },
  { key: "total_tips", label: "Tips", color: "#06b6d4" },
];

export default function TournamentDashboard() {
  const { cardroom } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [timeframe, setTimeframe] = useState("month");
  const [customDates, setCustomDates] = useState({ start: "", end: "" });
  const [view, setView] = useState("graph");
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [showTournaments, setShowTournaments] = useState(false);
  const [openTournaments, setOpenTournaments] = useState({});
  const [costModal, setCostModal] = useState({ isOpen: false, tournament: null });
  const [activityModal, setActivityModal] = useState({ isOpen: false, tournament: null });
  const [sortConfig, setSortConfig] = useState({ key: "profit", direction: "desc" });
  const [visibleSeries, setVisibleSeries] = useState({
    profit: true,
    total_rake: true,
    costs: true,
    total_tips: true,
  });

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealerdashboard`);
      return;
    }
    if (!permission?.includes("O") && !permission?.includes("Y")) {
      navigate(`/${cardroom}/game`);
      return;
    }
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  }, [token, cardroom, navigate, dealerMode, permission]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = { timeframe };
      if (timeframe === "custom") {
        params.startDate = customDates.start;
        params.endDate = customDates.end;
      }
      const { data: payload } = await axios.get(`/${cardroom}/api/tournament-dashboard/stats`, { params });
      setData(payload);
    } catch (err) {
      console.error("Failed to fetch tournament dashboard stats", err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [cardroom, timeframe, customDates]);

  useEffect(() => {
    if (token) fetchStats();
  }, [fetchStats, token]);

  const tournaments = data?.tournaments || [];
  const players = data?.players || [];
  const summary = data?.summary || {};

  const trendData = useMemo(() => {
    return [...tournaments].reverse().map((t) => ({
      ...t,
      axisLabel: chartLabel(t.created_at),
      dateLabel: formatDate(t.created_at),
      profit: Number(t.profit) || 0,
      total_rake: Number(t.total_rake) || 0,
      total_tips: Number(t.total_tips) || 0,
      costs: Number(t.costs) || 0,
      total_entries: Number(t.total_entries) || 0,
    }));
  }, [tournaments]);
  const useTournamentBarChart = tournaments.length === 1;

  const calendarDays = useMemo(() => {
    const byDay = tournaments.reduce((acc, t) => {
      const key = localDateKey(t.created_at);
      if (!key) return acc;
      if (!acc[key]) acc[key] = { tournaments: 0, profit: 0, rake: 0, tips: 0, entries: 0 };
      acc[key].tournaments += 1;
      acc[key].profit += Number(t.profit) || 0;
      acc[key].rake += Number(t.total_rake) || 0;
      acc[key].tips += Number(t.total_tips) || 0;
      acc[key].entries += Number(t.total_entries) || 0;
      return acc;
    }, {});

    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const leadingDays = firstDay.getDay();
    const slotCount = Math.ceil((leadingDays + daysInMonth) / 7) * 7;
    const todayKey = localDateKey(new Date());

    return Array.from({ length: slotCount }, (_, index) => {
      const date = new Date(year, month, index - leadingDays + 1);
      const dateKey = localDateKey(date);
      return {
        date,
        dateKey,
        data: byDay[dateKey],
        inCurrentMonth: date.getMonth() === month,
        isToday: dateKey === todayKey,
      };
    });
  }, [calendarMonth, tournaments]);

  const sortedPlayers = useMemo(() => {
    const sorted = [...players].sort((a, b) => {
      const aVal = sortConfig.key === "name" ? a.name : Number(a[sortConfig.key]) || 0;
      const bVal = sortConfig.key === "name" ? b.name : Number(b[sortConfig.key]) || 0;
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [players, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));
  };

  const toggleTournament = (id) => {
    setOpenTournaments((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="game">
      <TournamentHeader
        activePage="dashboard"
        cardroom={cardroom}
        centerTitle="Tournament Dashboard"
        title="Tournaments"
      />

      <div className="game-container">
        {loading && !data ? (
          <InitialLoadingScreen />
        ) : !data ? (
          <div className="game-card" style={{ padding: "2rem", textAlign: "center" }}>
            <h3>Could not load tournament dashboard.</h3>
            <button className="game-btn game-btn--primary" onClick={fetchStats}>Retry</button>
          </div>
        ) : tournaments.length === 0 ? (
          <>
            <div className="dashboard-controls" style={{ marginBottom: "2rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
                <div className="timeframe-selector" style={{ display: "flex", gap: "0.5rem", background: "#1e293b", padding: "0.4rem", borderRadius: "0.75rem", flexWrap: "wrap" }}>
                  {["last_session", "week", "month", "year", "custom"].map((tf) => (
                    <button
                      key={tf}
                      onClick={() => setTimeframe(tf)}
                      style={{
                        padding: "0.5rem 1rem",
                        borderRadius: "0.5rem",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        fontWeight: 700,
                        backgroundColor: timeframe === tf ? "#6366f1" : "transparent",
                        color: timeframe === tf ? "#fff" : "#94a3b8",
                      }}
                    >
                      {tf.replace("_", " ").toUpperCase()}
                    </button>
                  ))}
                </div>

                {timeframe === "custom" && (
                  <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
                    <input className="game-input" type="date" value={customDates.start} onChange={(e) => setCustomDates((p) => ({ ...p, start: e.target.value }))} />
                    <span style={{ color: "#94a3b8" }}>to</span>
                    <input className="game-input" type="date" value={customDates.end} onChange={(e) => setCustomDates((p) => ({ ...p, end: e.target.value }))} />
                    <button className="game-btn game-btn--primary" onClick={fetchStats}>Apply</button>
                  </div>
                )}
              </div>
            </div>
            <div style={{ padding: "4rem", textAlign: "center", background: "#1e293b", borderRadius: "1rem", border: "1px solid #334155" }}>
              <Calendar size={44} color="#94a3b8" />
              <h3 style={{ color: "#fff" }}>No Tournaments Found</h3>
              <p style={{ color: "#94a3b8" }}>Try a different timeframe.</p>
            </div>
          </>
        ) : (
          <>
            <div className="dashboard-controls" style={{ marginBottom: "2rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
                <div className="timeframe-selector" style={{ display: "flex", gap: "0.5rem", background: "#1e293b", padding: "0.4rem", borderRadius: "0.75rem", flexWrap: "wrap" }}>
                  {["last_session", "week", "month", "year", "custom"].map((tf) => (
                    <button
                      key={tf}
                      onClick={() => setTimeframe(tf)}
                      style={{
                        padding: "0.5rem 1rem",
                        borderRadius: "0.5rem",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        fontWeight: 700,
                        backgroundColor: timeframe === tf ? "#6366f1" : "transparent",
                        color: timeframe === tf ? "#fff" : "#94a3b8",
                      }}
                    >
                      {tf.replace("_", " ").toUpperCase()}
                    </button>
                  ))}
                </div>

                {timeframe === "custom" && (
                  <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
                    <input className="game-input" type="date" value={customDates.start} onChange={(e) => setCustomDates((p) => ({ ...p, start: e.target.value }))} />
                    <span style={{ color: "#94a3b8" }}>to</span>
                    <input className="game-input" type="date" value={customDates.end} onChange={(e) => setCustomDates((p) => ({ ...p, end: e.target.value }))} />
                    <button className="game-btn game-btn--primary" onClick={fetchStats}>Apply</button>
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
              <Metric title="Profit" value={money(summary.profit)} sub={`${money(summary.avgProfit)} avg`} icon={<DollarSign size={22} color="#10b981" />} color={(Number(summary.profit) || 0) >= 0 ? "#10b981" : "#f43f5e"} />
              <Metric title="Tournaments" value={summary.tournaments || 0} sub={`${(summary.avgEntries || 0).toFixed(1)} avg entries`} icon={<Trophy size={22} color="#60a5fa" />} />
              <Metric title="Entries" value={(summary.entries || 0).toLocaleString()} sub={`${summary.freeEntries || 0} free`} icon={<Users size={22} color="#fbbf24" />} />
              <Metric title="Rake" value={money(summary.rake)} sub={`${money(summary.tips)} tips`} icon={<Zap size={22} color="#8b5cf6" />} />
              <Metric title="Costs" value={money(summary.costs)} sub={`${money(summary.prizepool)} prizepool`} icon={<Receipt size={22} color="#f43f5e" />} />
            </div>

            <section style={{ background: "#1e293b", padding: "1.5rem", borderRadius: "1rem", border: "1px solid #334155", marginBottom: "1.5rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", marginBottom: "1rem", flexWrap: "wrap" }}>
                <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <Activity size={20} color="#60a5fa" /> Tournament Performance
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", alignItems: "flex-end" }}>
                  <div style={{ display: "flex", gap: "0.25rem", background: "#0f172a", border: "1px solid #334155", borderRadius: "0.5rem", padding: "0.25rem" }}>
                    {["graph", "calendar"].map((option) => (
                      <button
                        key={option}
                        onClick={() => setView(option)}
                        style={{
                          padding: "0.35rem 0.75rem",
                          borderRadius: "0.35rem",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          background: view === option ? "#6366f1" : "transparent",
                          color: view === option ? "#fff" : "#94a3b8",
                        }}
                      >
                        {option[0].toUpperCase() + option.slice(1)}
                      </button>
                    ))}
                  </div>
                  {view === "graph" && (
                    <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", flexWrap: "wrap" }}>
                      {GRAPH_SERIES.map((series) => (
                        <label
                          key={series.key}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.35rem",
                            cursor: "pointer",
                            fontSize: "0.72rem",
                            background: visibleSeries[series.key] ? `${series.color}22` : "#0f172a",
                            padding: "0.25rem 0.65rem",
                            borderRadius: "999px",
                            border: `1px solid ${visibleSeries[series.key] ? series.color : "#334155"}`,
                            color: visibleSeries[series.key] ? "#f8fafc" : "#94a3b8",
                            fontWeight: 700,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!visibleSeries[series.key]}
                            onChange={() => setVisibleSeries((prev) => ({ ...prev, [series.key]: !prev[series.key] }))}
                            style={{ display: "none" }}
                          />
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: visibleSeries[series.key] ? series.color : "#475569" }} />
                          {series.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {view === "graph" ? (
                <div>
                  <div style={{ height: 340 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      {useTournamentBarChart ? (
                        <BarChart data={trendData} margin={{ top: 10, right: 20, left: 8, bottom: 18 }} barGap={6} barCategoryGap="18%">
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="axisLabel" stroke="#94a3b8" tick={<TournamentXAxisTick />} interval={0} height={48} />
                          <YAxis stroke="#94a3b8" tickFormatter={compactMoney} />
                          {GRAPH_SERIES.map((series) => visibleSeries[series.key] && (
                            <Bar
                              key={series.key}
                              dataKey={series.key}
                              name={series.label}
                              fill={series.color}
                              radius={[4, 4, 0, 0]}
                              maxBarSize={42}
                            />
                          ))}
                        </BarChart>
                      ) : (
                        <AreaChart data={trendData} margin={{ top: 10, right: 20, left: 8, bottom: 18 }}>
                          <defs>
                            <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="axisLabel" stroke="#94a3b8" tick={<TournamentXAxisTick />} interval={0} height={48} />
                          <YAxis stroke="#94a3b8" tickFormatter={compactMoney} />
                          <Tooltip content={<TournamentTooltip />} />
                          {GRAPH_SERIES.map((series) => visibleSeries[series.key] && (
                            <Area
                              key={series.key}
                              type="monotone"
                              dataKey={series.key}
                              name={series.label}
                              stroke={series.color}
                              fill={series.key === "profit" ? "url(#profitGradient)" : "transparent"}
                              strokeWidth={series.key === "profit" ? 3 : 2}
                            />
                          ))}
                        </AreaChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                  {useTournamentBarChart && (
                    <div style={{ display: "flex", justifyContent: "center", gap: "0.75rem", flexWrap: "wrap", marginTop: "0.85rem" }}>
                      {GRAPH_SERIES
                        .filter((series) => visibleSeries[series.key])
                        .map((series) => {
                          const value = Number(trendData[0]?.[series.key]) || 0;
                          return (
                            <div
                              key={series.key}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.4rem",
                                padding: "0.35rem 0.65rem",
                                borderRadius: "999px",
                                background: "#0f172a",
                                border: `1px solid ${series.color}`,
                                color: "#f8fafc",
                                fontSize: "0.78rem",
                                fontWeight: 700,
                              }}
                            >
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: series.color }} />
                              <span>{series.label}</span>
                              <span style={{ color: series.color }}>{money(value)}</span>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              ) : (
                <TournamentCalendar
                  days={calendarDays}
                  month={calendarMonth}
                  setMonth={setCalendarMonth}
                />
              )}
            </section>

            <section style={{ background: "#1e293b", padding: "1.5rem", borderRadius: "1rem", border: "1px solid #334155", marginBottom: "1.5rem" }}>
              <div
                onClick={() => setShowTournaments((open) => !open)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: showTournaments ? "1rem" : "0",
                  padding: "0.5rem",
                  borderRadius: "0.5rem",
                  transition: "all 0.2s",
                  cursor: "pointer",
                }}
                className="hover-bg"
              >
                <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <Trophy size={20} color="#fbbf24" /> Tournaments ({tournaments.length})
                </h3>
                <div style={{ color: "#94a3b8" }}>
                  {showTournaments ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </div>
              </div>
              {showTournaments && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {tournaments.map((t) => {
                  const isOpen = !!openTournaments[t.id];
                  const payouts = (t.payouts || []).filter((p) => (Number(p.cash) || 0) > 0 || (Number(p.points) || 0) > 0);
                  const customAllocations = parseCustomAllocations(t.custom_allocations_json);
                  return (
                    <div key={t.id} style={{ border: isOpen ? "2px solid #60a5fa" : "1px solid #334155", borderRadius: "0.75rem", background: "#0f172a", overflow: "hidden" }}>
                      <button
                        type="button"
                        onClick={() => toggleTournament(t.id)}
                        style={{ width: "100%", border: "none", background: "transparent", color: "inherit", cursor: "pointer", padding: "1rem", display: "grid", gridTemplateColumns: "1fr auto", gap: "1rem", alignItems: "center", textAlign: "left" }}
                      >
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.35rem", flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 800, color: "#f8fafc", fontSize: "1.05rem" }}>{t.name}</span>
                            <span style={{ fontSize: "0.72rem", background: t.state === "finished" ? "#334155" : "#10b981", color: "#fff", padding: "2px 8px", borderRadius: "999px", fontWeight: 700 }}>{t.state}</span>
                          </div>
                          <div style={{ color: "#94a3b8", fontSize: "0.9rem" }}>
                            {formatDate(t.created_at)} - {t.buyins_num || 0} unique entries - {t.rebuys_num || 0} rebuys - {t.total_entries || 0} total entries - {money(t.prizepool)} prizepool
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ color: (Number(t.profit) || 0) >= 0 ? "#10b981" : "#f43f5e", fontWeight: 800 }}>{money(t.profit)}</div>
                            <div style={{ color: "#94a3b8", fontSize: "0.8rem" }}>{money(t.total_rake)} rake</div>
                          </div>
                          {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        </div>
                      </button>

                      {isOpen && (
                        <div style={{ padding: "1rem", borderTop: "1px solid #334155", background: "rgba(255,255,255,0.02)" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", marginBottom: "1rem" }}>
                            <h4 style={{ margin: 0, color: "#f8fafc", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                              {t.name}
                              <button
                                type="button"
                                className="game-icon-btn game-icon-btn--info"
                                title="View tournament activity"
                                onClick={() => setActivityModal({ isOpen: true, tournament: t })}
                              >
                                <FaInfo />
                              </button>
                            </h4>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.75rem", marginBottom: "1rem" }}>
                            <Mini label="Tips" value={money(t.total_tips)} />
                            <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "0.65rem", padding: "0.85rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.6rem" }}>
                              <div>
                                <div style={{ color: "#94a3b8", fontSize: "0.72rem", textTransform: "uppercase", fontWeight: 800, marginBottom: "0.25rem" }}>Costs</div>
                                <div style={{ color: "#f8fafc", fontWeight: 800 }}>{money(t.costs)}</div>
                              </div>
                              <button
                                type="button"
                                className="game-icon-btn game-icon-btn--info"
                                title="View tournament costs"
                                onClick={() => setCostModal({ isOpen: true, tournament: t })}
                              >
                                <FaInfo />
                              </button>
                            </div>
                            <Mini label="Ending Cash" value={money(t.ending_cash)} />
                            <Mini label="Free Entries" value={t.free_entries || 0} />
                            <Mini label="Add-ons" value={t.addons_num || 0} />
                            {customAllocations.map((allocation) => (
                              <Mini key={allocation.id || allocation.name} label={allocation.name} value={money(allocation.total)} />
                            ))}
                          </div>
                          <h4 style={{ margin: "0 0 0.75rem", color: "#f8fafc" }}>Placements</h4>
                          {payouts.length === 0 ? (
                            <p style={{ color: "#64748b", margin: 0 }}>No player results recorded yet.</p>
                          ) : (
                            <div style={{ overflowX: "auto" }}>
                              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                                <thead>
                                  <tr style={{ color: "#94a3b8", borderBottom: "1px solid #334155" }}>
                                    <th style={{ padding: "0.6rem", textAlign: "center" }}>Place</th>
                                    <th style={{ padding: "0.6rem", textAlign: "center" }}>Player</th>
                                    <th style={{ padding: "0.6rem", textAlign: "center" }}>Points</th>
                                    <th style={{ padding: "0.6rem", textAlign: "center" }}>Cash</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {payouts.slice(0, 10).map((p) => (
                                    <tr key={`${t.id}-${p.player_id}`} style={{ borderBottom: "1px solid rgba(51,65,85,0.5)" }}>
                                      <td style={{ padding: "0.6rem", color: "#fbbf24", fontWeight: 700 }}>{p.place || "-"}</td>
                                      <td style={{ padding: "0.6rem", color: "#f8fafc", fontWeight: 600 }}>{p.name}</td>
                                      <td style={{ padding: "0.6rem", textAlign: "center" }}>{p.points || 0}</td>
                                      <td style={{ padding: "0.6rem", textAlign: "center", color: "#10b981", fontWeight: 700 }}>{money(p.cash)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </section>

            <section style={{ background: "#1e293b", padding: "1.5rem", borderRadius: "1rem", border: "1px solid #334155", marginBottom: "2rem" }}>
              <h3 style={{ margin: "0 0 1rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Users size={20} color="#60a5fa" /> Tournament Players ({players.length})
              </h3>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "#94a3b8", borderBottom: "1px solid #334155" }}>
                      {[
                        ["name", "Player"],
                        ["tournaments_played", "Tournaments"],
                        ["entries", "Entries"],
                        ["free_entries", "Free Entries"],
                        ["deposits", "Deposits"],
                        ["tips", "Tips"],
                        ["cash", "Cashed"],
                        ["points", "Points"],
                        ["cashes", "Cashes"],
                        ["best_place", "Best Place"],
                        ["profit", "Profit"],
                      ].map(([key, label]) => (
                        <th key={key} onClick={() => requestSort(key)} style={{ padding: "0.75rem", cursor: "pointer", textAlign: "center" }}>
                          {label}<SortIndicator active={sortConfig.key === key} direction={sortConfig.direction} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPlayers.map((p) => (
                      <tr key={p.player_id} style={{ borderBottom: "1px solid #0f172a" }}>
                        <td style={{ padding: "0.75rem", textAlign: "center", color: "#f8fafc", fontWeight: 700 }}>{p.name}</td>
                        <td style={{ padding: "0.75rem", textAlign: "center" }}>{p.tournaments_played || 0}</td>
                        <td style={{ padding: "0.75rem", textAlign: "center" }}>{p.entries || 0}</td>
                        <td style={{ padding: "0.75rem", textAlign: "center" }}>{p.free_entries || 0}</td>
                        <td style={{ padding: "0.75rem", textAlign: "center", color: "#fbbf24" }}>{money(p.deposits)}</td>
                        <td style={{ padding: "0.75rem", textAlign: "center", color: "#06b6d4" }}>{money(p.tips)}</td>
                        <td style={{ padding: "0.75rem", textAlign: "center", color: "#10b981" }}>{money(p.cash)}</td>
                        <td style={{ padding: "0.75rem", textAlign: "center" }}>{p.points || 0}</td>
                        <td style={{ padding: "0.75rem", textAlign: "center" }}>{p.cashes || 0}</td>
                        <td style={{ padding: "0.75rem", textAlign: "center" }}>{p.best_place || "-"}</td>
                        <td style={{ padding: "0.75rem", textAlign: "center", color: (Number(p.profit) || 0) >= 0 ? "#10b981" : "#f43f5e", fontWeight: 800 }}>{money(p.profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
      {costModal.isOpen && (
        <TournamentCostModal
          tournament={costModal.tournament}
          onClose={() => setCostModal({ isOpen: false, tournament: null })}
        />
      )}
      {activityModal.isOpen && (
        <TournamentActivityModal
          tournament={activityModal.tournament}
          onClose={() => setActivityModal({ isOpen: false, tournament: null })}
        />
      )}
    </div>
  );
}

function Metric({ title, value, sub, icon, color = "#f8fafc" }) {
  return (
    <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "0.85rem", padding: "1rem", boxShadow: "0 10px 18px rgba(0,0,0,0.16)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.65rem" }}>
        <span style={{ color: "#94a3b8", fontSize: "0.78rem", fontWeight: 800, textTransform: "uppercase" }}>{title}</span>
        {icon}
      </div>
      <div style={{ color, fontSize: "1.55rem", fontWeight: 900, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ color: "#94a3b8", fontSize: "0.8rem", marginTop: "0.4rem" }}>{sub}</div>}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "0.5rem", padding: "0.75rem" }}>
      <div style={{ color: "#64748b", fontSize: "0.7rem", fontWeight: 800, textTransform: "uppercase" }}>{label}</div>
      <div style={{ color: "#f8fafc", fontWeight: 800, marginTop: "0.2rem" }}>{value}</div>
    </div>
  );
}

function TournamentCostModal({ tournament, onClose }) {
  const costs = tournament?.costs_history || [];
  return (
    <>
      <div className="game-modal-overlay" onClick={onClose} />
      <div className="game-modal game-modal--large">
        <div className="game-modal__header">
          <h3 className="game-modal__title">{tournament?.name || "Tournament"} Costs</h3>
          <button className="game-icon-btn" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="game-modal__body game-modal__body--scroll">
          {costs.length === 0 ? (
            <p className="game-empty">No costs recorded yet</p>
          ) : (
            <div className="game-transaction-list">
              {costs.map((cost) => (
                <div key={cost.id} className="game-transaction-item">
                  <span className="game-transaction-time">
                    {new Date(cost.completed_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                  </span>
                  <span className="game-transaction-amount">{money(cost.amount)}</span>
                  <span className="game-transaction-type">{cost.notes || "Cost"}</span>
                  <span className="game-transaction-method">{cost.wallet_name ? `Wallet: ${cost.wallet_name}` : "Cash"}</span>
                  <span className="game-transaction-date">
                    {new Date(cost.completed_at).toLocaleDateString(undefined, { month: "numeric", day: "numeric" })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function TournamentActivityModal({ tournament, onClose }) {
  return (
    <>
      <div className="game-modal-overlay" onClick={onClose} />
      <div className="game-modal game-modal--large">
        <div className="game-modal__header">
          <h3 className="game-modal__title">{tournament?.name || "Tournament"} Activity</h3>
          <button className="game-icon-btn" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="game-modal__body game-modal__body--scroll">
          <TournamentActivityList
            transactions={tournament?.transactions || []}
            emptyText="No transactions found."
          />
        </div>
      </div>
    </>
  );
}

function SortIndicator({ active, direction }) {
  if (!active) return null;
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 0,
        height: 0,
        marginLeft: "0.35rem",
        verticalAlign: "middle",
        borderLeft: "4px solid transparent",
        borderRight: "4px solid transparent",
        borderBottom: direction === "asc" ? "6px solid #94a3b8" : "none",
        borderTop: direction === "desc" ? "6px solid #94a3b8" : "none",
      }}
    />
  );
}

function TournamentTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div style={{ background: "#0f172a", border: "1px solid #334155", borderRadius: "0.75rem", padding: "0.85rem", color: "#e2e8f0" }}>
      <div style={{ fontWeight: 800, marginBottom: "0.35rem" }}>{row.name}</div>
      <div style={{ color: "#94a3b8", fontSize: "0.8rem", marginBottom: "0.5rem" }}>{String(label).replace("|", ", ")}</div>
      {payload.map((item) => (
        <div key={item.dataKey}>
          {item.name}: <b style={{ color: item.color }}>{money(item.value)}</b>
        </div>
      ))}
      <div>Entries: <b>{row.total_entries || 0}</b></div>
    </div>
  );
}

function TournamentXAxisTick({ x, y, payload }) {
  const [day, date] = String(payload?.value || "").split("|");
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill="#94a3b8" fontSize="11" fontWeight="700">
        <tspan x="0" dy="0">{date || ""}</tspan>
        <tspan x="0" dy="14" fill="#64748b" fontSize="10">{day || ""}</tspan>
      </text>
    </g>
  );
}

function TournamentCalendar({ days, month, setMonth }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "0.75rem", marginBottom: "1rem" }}>
        <button className="game-icon-btn game-icon-btn--sm" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}><FaChevronLeft /></button>
        <strong style={{ color: "#f8fafc", minWidth: 150, textAlign: "center" }}>{month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</strong>
        <button className="game-icon-btn game-icon-btn--sm" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}><FaChevronRight /></button>
      </div>
      <div style={{ overflowX: "auto", paddingBottom: "0.25rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(92px, 1fr))", gap: "0.5rem", minWidth: "720px" }}>
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} style={{ color: "#94a3b8", fontSize: "0.7rem", fontWeight: 800, textTransform: "uppercase", padding: "0 0.35rem" }}>{day}</div>
          ))}
          {days.map((day) => {
            const hasData = Boolean(day.data?.tournaments);
            const profit = Number(day.data?.profit) || 0;
            const profitColor = profit >= 0 ? "#10b981" : "#f43f5e";
            return (
              <div
                key={day.dateKey}
                style={{
                  minHeight: 126,
                  padding: "0.6rem",
                  borderRadius: "0.5rem",
                  border: day.isToday ? "1px solid #60a5fa" : `1px solid ${hasData ? "#475569" : "#334155"}`,
                  background: hasData ? "rgba(15,23,42,0.95)" : "rgba(15,23,42,0.45)",
                  opacity: day.inCurrentMonth ? 1 : 0.42,
                  boxShadow: hasData ? `inset 0 3px 0 ${profitColor}` : "none",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                  <b style={{ color: day.isToday ? "#93c5fd" : "#cbd5e1" }}>{day.date.getDate()}</b>
                  {hasData && <b style={{ color: profitColor }}>{compactMoney(profit)}</b>}
                </div>
                {hasData && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.35rem" }}>
                    <CalendarMetric label="Events" value={day.data.tournaments} />
                    <CalendarMetric label="Entries" value={day.data.entries} />
                    <CalendarMetric label="Rake" value={compactMoney(day.data.rake)} />
                    <CalendarMetric label="Tips" value={compactMoney(day.data.tips)} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CalendarMetric({ label, value }) {
  return (
    <div style={{ background: "rgba(30,41,59,0.85)", border: "1px solid rgba(71,85,105,0.75)", borderRadius: "0.4rem", padding: "0.35rem" }}>
      <div style={{ color: "#64748b", fontSize: "0.62rem", fontWeight: 800, textTransform: "uppercase" }}>{label}</div>
      <div style={{ color: "#e2e8f0", fontSize: "0.78rem", fontWeight: 800 }}>{value}</div>
    </div>
  );
}
